import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Tab, Tabs } from 'react-bootstrap';

import { ReactSelectOption } from '../Selects/SortableSelect';
import ValidationAlert from '../../../app/components/ValidationAlert';
import {
  CreateUpdateUserDto,
  GetHealthcareFacilityDto,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { useAuth } from '../../../common/hooks/useAuth';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { useSpdCore } from '../../../common/hooks/useSpdCore';

import { UserEditorTabData } from './UserEditorTabData';
import { UserEditorTabDoctor } from './UserEditorTabDoctor';
import { UserEditorTabNew } from './UserEditorTabNew';

interface UserEditorProps {
  id?: string;
  user: CreateUpdateUserDto;
  hasCertificate?: boolean;
  handleClose?: () => void;
  show?: boolean;
  healthcareFacilities?: (GetHealthcareFacilityDto | null | undefined)[];
  onCompleted: (
    examined: CreateUpdateUserDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const UserEditor: React.FC<UserEditorProps> = props => {
  const [user, setUser] = useState<CreateUpdateUserDto | undefined>(props.user);
  const [adminReadOnly, setAdminReadOnly] = useState(false);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [healthCareFacilities, setHealthcareFacilities] = useState<
    ReactSelectOption[]
  >([]);
  const auth = useAuth();
  const spd = useSpdCore();

  useEffect(() => {
    const canEditAdmin =
      auth.userProfile?.admin || auth.userProfile?.superadmin;
    const editingSelf = auth.userProfile?.id === props.id;
    setAdminReadOnly(!canEditAdmin || editingSelf);
  }, [
    auth.userProfile?.admin,
    auth.userProfile?.superadmin,
    auth.userProfile?.id,
    props.id,
  ]);

  useEffect(() => {
    if (!props.show) {
      setUser(undefined);
    }
    if (props.show) {
      setUser(props.user);
      setSaveErrors([]);
      setShowSaveErrors(false);
      const fac =
        props.healthcareFacilities
          ?.filter(b => !!b)
          ?.map(b => ({
            value: b?.id || '',
            label: b?.displayName || '',
          })) || [];
      setHealthcareFacilities(fac);
    }
  }, [props.user, props.id, props.show, props.healthcareFacilities]);

  const createUser = async () => {
    if (user) {
      props
        .onCompleted(user, props.id)
        .then(saveStatus => {
          if (!saveStatus.saved) {
            setSaveErrors(saveStatus.errors);
            setShowSaveErrors(true);
            return;
          } else {
            setShowSaveErrors(false);
          }
        })
        .catch(error => {
          setSaveErrors(error);
          setShowSaveErrors(true);
        });
    }
  };

  const propertyChange = (obj: Partial<CreateUpdateUserDto>) => {
    setUser({ ...user, ...obj });
  };

  const handleChangeHF = async (tempHfs: ReactSelectOption[]) => {
    setUser({ ...user, healthcareFacilities: tempHfs.map(o => o.value) });
    setHealthcareFacilities(tempHfs);
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id ? 'Edycja użytkownika' : 'Dodawanie nowego użytkownika'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs defaultActiveKey="data" id="userEditorTabs" className="mb-2">
            <Tab eventKey="data" title="Dane podstawowe" className="mb-2">
              <UserEditorTabData
                id={props.id}
                user={user}
                adminReadOnly={adminReadOnly}
                healthCareFacilities={healthCareFacilities}
                propertyChange={propertyChange}
                handleChangeHF={handleChangeHF}
              />
            </Tab>
            <Tab eventKey="doctor" title="Lekarz/Psycholog" className="mb-2">
              <UserEditorTabDoctor
                user={user}
                propertyChange={propertyChange}
              />
            </Tab>
            <Tab eventKey="new" title="Rejestrator zewnętrzny" className="mb-2">
              <UserEditorTabNew user={user} propertyChange={propertyChange} />
            </Tab>
          </Tabs>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="mt-3"
          />
        </Modal.Body>
        <Modal.Footer>
          {props.id && (
            <LastEditorInfo id={props.id} method={spd.users.getLastEditor} />
          )}
          <Button variant="outline-secondary" onClick={props.handleClose}>
            Zamknij
          </Button>
          <Button variant="outline-primary" onClick={createUser}>
            Zapisz
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
